import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, LocateFixed, LogIn, LogOut, MapPin, RefreshCw, TriangleAlert, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { APPROXIMATE_ACCURACY_M, geolocationErrorMessage, getCurrentPosition, locationSettingsSteps, queryLocationPermission } from '../lib/geo';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { uploadSelfie } from '../lib/upload';

export type AttendanceRecord = {
  id: string; workDate: string; checkInAt?: string; checkOutAt?: string; status: string; checkInMode?: 'geofence' | 'roaming' | 'wfh' | 'anywhere' | null;
  checkInSelfieUrl?: string | null; checkOutSelfieUrl?: string | null; checkInLat?: string | null; checkInLng?: string | null; checkOutLat?: string | null; checkOutLng?: string | null;
};
type MyBranch = { branchId: string; branchName: string; isRoaming: boolean; checkInMode: 'geofence' | 'roaming' | 'wfh' | 'anywhere'; wfhUntil: string | null; allowedBranches: { id: string; name: string }[] } | null;

/**
 * A live `getUserMedia` capture, not a file picker. `<input type="file" capture="user">`
 * only ever *hints* mobile browsers to jump into the camera app — desktop Chrome ignores the
 * hint entirely and just opens a normal file-open dialog, so it never prompts for camera
 * access and quietly lets anyone pick an old photo off disk. That defeats "camera only, old
 * gallery photos cannot be used" (SoW §6.1). Requesting the camera stream directly works the
 * same way on desktop and mobile, actually triggers the permission prompt, and makes picking
 * an existing file impossible — there's no file input in this component at all.
 */
function SelfieCapture({ file, onChange }: { file: File | null; onChange: (file: File | null) => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [active, setActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preview = file ? URL.createObjectURL(file) : null;

  function stopCamera() {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setActive(false);
  }

  useEffect(() => () => stopCamera(), []);

  async function openCamera() {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia) { setError('This browser cannot access the camera. Try Chrome or Safari over HTTPS.'); return; }
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      setActive(true);
    } catch (err) {
      setError(err instanceof Error && err.name === 'NotAllowedError' ? "Camera access was blocked. Allow it in your browser's site settings and try again." : 'Could not access the camera on this device.');
    }
  }

  useEffect(() => {
    if (active && videoRef.current && streamRef.current) { videoRef.current.srcObject = streamRef.current; void videoRef.current.play(); }
  }, [active]);

  function capture() {
    const video = videoRef.current;
    if (!video) return;
    // Downscale before it ever leaves the device — a raw front-camera frame is easily 2-4MP;
    // nothing here needs more than a few hundred pixels to verify a face and location later.
    // Matters most on the slow rural connections this app targets (Infra And Tech.md §10).
    const MAX_DIMENSION = 640;
    const scale = Math.min(1, MAX_DIMENSION / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => { if (blob) onChange(new File([blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' })); stopCamera(); }, 'image/jpeg', 0.8);
  }

  if (file) {
    return (
      <div className="flex flex-col items-center gap-2">
        <img src={preview!} alt="Selfie preview" className="h-28 w-28 rounded-xl border border-border object-cover" />
        <button type="button" className="btn-secondary" onClick={() => onChange(null)}><Camera size={18} /> Retake selfie</button>
      </div>
    );
  }

  if (active) {
    // A full-screen takeover, not an inline preview box — this is the important-feature
    // "make it right" case (an anti-fraud check-in photo), so it gets the same full-page
    // camera treatment a real camera app would, not a small thumbnail buried in a card.
    // Full-bleed on mobile; on large screens it settles into a centered panel instead of
    // stretching a selfie feed across a wide desktop viewport.
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-black lg:items-center lg:justify-center lg:bg-black/80 lg:p-6">
        <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden lg:h-auto lg:max-h-[85vh] lg:w-auto lg:max-w-sm lg:flex-none lg:rounded-2xl lg:aspect-[3/4]">
          <video ref={videoRef} muted playsInline style={{ transform: 'scaleX(-1)' }} className="h-full w-full flex-1 bg-canvas object-cover" />
          <button type="button" aria-label="Cancel" onClick={stopCamera} className="absolute right-4 top-[calc(env(safe-area-inset-top)+1rem)] flex size-11 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur">
            <X size={22} />
          </button>
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-center bg-gradient-to-t from-black/60 to-transparent pb-[calc(env(safe-area-inset-bottom)+2rem)] pt-10">
            <button type="button" aria-label="Capture selfie" onClick={capture} className="flex size-20 items-center justify-center rounded-full border-4 border-white/90 bg-white/10 backdrop-blur active:scale-95">
              <span className="size-16 rounded-full bg-white" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <button type="button" className="btn-secondary" onClick={() => void openCamera()}><Camera size={18} /> Take selfie (required)</button>
      {error && <p className="max-w-xs text-sm text-red-700">{error}</p>}
    </div>
  );
}

const formatAccuracy = (metres: number) => (metres < 1000 ? `${Math.round(metres)} m` : `${(metres / 1000).toFixed(1)} km`);

type LocationState =
  | { kind: 'checking' }
  | { kind: 'ok'; accuracy: number }
  | { kind: 'approximate'; accuracy: number }
  | { kind: 'denied' }
  | { kind: 'unavailable'; message: string };

/**
 * Checks location as soon as the card opens instead of only at submit, so the browser's permission
 * prompt shows up while the person is looking at it, and an "approximate location" phone is caught
 * with fix-it steps up front rather than surfacing later as a baffling "outside the geofence" error.
 */
function LocationStatus({ needsPrecise, branchName }: { needsPrecise: boolean; branchName?: string }) {
  const [state, setState] = useState<LocationState>({ kind: 'checking' });

  async function check() {
    setState({ kind: 'checking' });
    try {
      const position = await getCurrentPosition();
      const { accuracy } = position.coords;
      setState(accuracy > APPROXIMATE_ACCURACY_M ? { kind: 'approximate', accuracy } : { kind: 'ok', accuracy });
    } catch (err) {
      if (err instanceof GeolocationPositionError && err.code === err.PERMISSION_DENIED) setState({ kind: 'denied' });
      else setState({ kind: 'unavailable', message: geolocationErrorMessage(err) });
    }
  }

  useEffect(() => {
    // Once blocked, the browser won't prompt again — skip straight to the settings steps.
    void queryLocationPermission().then((permission) => (permission === 'denied' ? setState({ kind: 'denied' }) : check()));
  }, []);

  if (state.kind === 'checking') return <p className="flex items-center justify-center gap-2 text-xs text-muted"><RefreshCw size={14} className="animate-spin" /> Checking your location…</p>;
  if (state.kind === 'ok' || (state.kind === 'approximate' && !needsPrecise)) {
    return <p className="flex items-center justify-center gap-1.5 text-xs font-medium text-green-700"><MapPin size={14} /> Location on · accurate to {formatAccuracy(state.accuracy)}</p>;
  }

  const problem = state.kind === 'approximate' ? 'approximate' : 'denied';
  const title = state.kind === 'approximate'
    ? `Your phone is sharing approximate location (±${formatAccuracy(state.accuracy)})`
    : state.kind === 'denied' ? 'Location access is blocked' : state.message;
  const detail = state.kind === 'approximate'
    ? `Checking in${branchName ? ` at ${branchName}` : ''} needs precise location. Turn it on for this browser:`
    : state.kind === 'denied' ? 'Attendance needs your location. Allow it for this browser:' : 'Check that location is turned on:';
  return (
    <div className="w-full max-w-sm rounded-xl border border-amber-300 bg-amber-50 p-3 text-left text-sm text-amber-900">
      <p className="flex items-start gap-2 font-semibold"><TriangleAlert size={16} className="mt-0.5 shrink-0" /> {title}</p>
      <p className="mt-1">{detail}</p>
      <ol className="mt-2 list-decimal space-y-1 pl-5">{locationSettingsSteps(problem).map((step) => <li key={step}>{step}</li>)}</ol>
      <button type="button" className="btn-secondary mt-3 w-full" onClick={() => void check()}><RefreshCw size={16} /> Check again</button>
    </div>
  );
}

/** The big, unmissable check-in/out widget — used on both the Dashboard and the Attendance page. */
export function AttendanceCard({ size = 'lg' }: { size?: 'lg' | 'md' }) {
  const client = useQueryClient();
  const { error: toastError, success } = useToast();
  const [selfie, setSelfie] = useState<File | null>(null);
  const query = useQuery({ queryKey: ['attendance'], queryFn: async () => (await api.get<AttendanceRecord[]>('/api/attendance')).data });
  const myBranch = useQuery({ queryKey: ['me', 'branch'], queryFn: async () => (await api.get<MyBranch>('/api/me/branch')).data });

  const punch = useMutation({
    mutationFn: async (kind: 'in' | 'out') => {
      if (!selfie) throw new Error('Take a selfie before you can check in or out.');
      // Location is captured on every punch — WFH / roaming / general-head only skip the geofence
      // check on the server, they never skip recording where the punch came from.
      const position = await getCurrentPosition().catch((err) => { throw new Error(geolocationErrorMessage(err)); });
      const coords = { lat: position.coords.latitude, lng: position.coords.longitude };
      const selfieUrl = await uploadSelfie(selfie);
      if (kind === 'in') {
        if (!myBranch.data) throw new Error('No active branch assignment. Ask HR to assign you to a branch.');
        // A coarse fix can't be matched to a 250 m geofence — stop here with the real reason instead of
        // sending it and getting back "Outside the allowed branch geofence".
        if (myBranch.data.checkInMode === 'geofence' && position.coords.accuracy > APPROXIMATE_ACCURACY_M) {
          throw new Error(`Your phone is sharing approximate location (±${formatAccuracy(position.coords.accuracy)}). Turn on Precise Location for this browser using the steps on this card, then try again.`);
        }
        return api.post('/api/attendance/check-in', { branchId: myBranch.data.branchId, ...coords, accuracy: position.coords.accuracy, selfieUrl });
      }
      return api.post('/api/attendance/check-out', { ...coords, selfieUrl });
    },
    onSuccess: (_data, kind) => {
      setSelfie(null);
      void client.invalidateQueries({ queryKey: ['attendance'] });
      success(kind === 'in' ? 'Checked in' : 'Checked out');
    },
    onError: (err) => toastError(err instanceof Error && !('response' in err) ? err.message : apiErrorMessage(err, 'Could not record attendance')),
  });

  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
  // A night shift checked in yesterday is still "today's" shift until checked out — same 20 h
  // window the server uses to find the punch to close (workflows/handlers.ts findOpenPunch).
  const openPunch = query.data?.find((row) => row.checkInAt && !row.checkOutAt && Date.now() - new Date(row.checkInAt).getTime() < 20 * 60 * 60 * 1000);
  const current = openPunch ?? query.data?.find((row) => row.workDate === today);
  const isCheckedIn = Boolean(current?.checkInAt);
  const isComplete = Boolean(current?.checkOutAt);
  const big = size === 'lg';
  const policyHint = (() => {
    const b = myBranch.data; if (!b) return null;
    if (b.checkInMode === 'wfh') return `Work from home approved until ${new Date(`${b.wfhUntil}T00:00:00`).toLocaleDateString('en-IN', { dateStyle: 'medium' })} — keep GPS on; your location is still recorded, just not matched to a branch.`;
    if (b.checkInMode === 'anywhere') return 'You can check in from anywhere.';
    if (b.checkInMode === 'roaming') return 'Roaming staff — you can check in from any site.';
    if (b.allowedBranches.length > 1) return `You can check in at any of your ${b.allowedBranches.length} branches: ${b.allowedBranches.map((x) => x.name).join(', ')}.`;
    return null;
  })();

  return (
    <section className={`card text-center ${big ? 'p-8' : ''}`}>
      <LocateFixed className="mx-auto text-brand-600" size={big ? 40 : 30} />
      <h2 className={`mt-3 font-bold ${big ? 'text-2xl' : 'text-xl'}`}>{isComplete ? 'Day complete' : isCheckedIn ? 'Checked in' : 'Ready to check in'}</h2>
      <p className="mt-1 text-sm text-muted">
        {myBranch.isLoading ? 'Loading branch…' : myBranch.data ? myBranch.data.branchName : 'No branch assigned yet'}
        {current?.checkInAt && ` • In: ${new Date(current.checkInAt).toLocaleTimeString('en-IN')}`}
        {current?.checkOutAt && ` • Out: ${new Date(current.checkOutAt).toLocaleTimeString('en-IN')}`}
      </p>
      {policyHint && !isComplete && <p className="mt-2 text-xs font-medium text-brand-700">{policyHint}</p>}

      {!isComplete && (
        <div className="mt-5 flex flex-col items-center gap-4">
          {myBranch.data && <LocationStatus needsPrecise={!isCheckedIn && myBranch.data.checkInMode === 'geofence'} branchName={myBranch.data.branchName} />}
          <SelfieCapture file={selfie} onChange={setSelfie} />
          <button
            className={`btn-primary w-full sm:w-auto ${big ? '!min-h-14 !px-8 !text-lg' : ''}`}
            disabled={punch.isPending || !selfie || (!isCheckedIn && !myBranch.data)}
            onClick={() => punch.mutate(isCheckedIn ? 'out' : 'in')}
          >
            {punch.isPending ? <RefreshCw size={big ? 22 : 19} className="animate-spin" /> : isCheckedIn ? <LogOut size={big ? 22 : 19} /> : <LogIn size={big ? 22 : 19} />}
            {isCheckedIn ? 'Check out' : 'Check in'}
          </button>
        </div>
      )}
    </section>
  );
}
